import React, { useMemo } from 'react';
import { AccountInfo } from "@azure/msal-browser";
import { AuthType, clearLoginStorage, persistAuthType } from "@shared/utils/AuthUtils";
import { useMsal } from "@azure/msal-react";
import { useDispatch } from "react-redux";
import { TrackJS } from "trackjs";
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { setAuthLoading, setUser } from "@shared/store/auth.duck";
import { NotificationMessages } from "@shared/models/notification.models";
import { loginActions } from "./login.duck";
import { classifyAuthError, enrichError } from "@shared/utils/error-helper";
import { bootstrapAuth, loginWithMicrosoftPopup } from '@shared/auth/authMachine.duck';
import { rawUserRequest } from '@shared/services/Auth';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

export function MicrosoftTeamsLoginButton(props: Props) {
  const {instance} = useMsal();
  const dispatch = useDispatch();
  const accounts = useMemo(() => instance.getAllAccounts(), [instance]);

  const onClick = async (_account?: AccountInfo) => {
    clearLoginStorage();
    persistAuthType(AuthType.Microsoft, dispatch);
    dispatch(setAuthLoading(true));

    dispatch(loginActions.clearSmartError());

    try {
      try {
        await (dispatch(bootstrapAuth({ source: 'route-guard' })) as any).unwrap();
      } catch (silentErr: any) {
        // Silent SSO failed. Cancellation is a deliberate user action, so don't
        // retry; everything else (incl. broker errors in Outlook) gets a popup
        // fallback that drives the same flow through authentication.authenticate.
        const silentCode = classifyAuthError(silentErr);
        if (silentCode === NotificationMessages.cancelledByUser) throw silentErr;
        TrackJS?.track(`[MicrosoftTeamsLoginButton] silent failed, falling back to popup: code=${silentCode}`);
        await (dispatch(loginWithMicrosoftPopup()) as any).unwrap();
      }
      const userResp = await rawUserRequest();
      dispatch(setUser(userResp));
    } catch (e: any) {
      const code = classifyAuthError(e);
      if (code === NotificationMessages.unknownError) {
        console.log(JSON.stringify(e));
        TrackJS?.track(e);
      } else {
        TrackJS?.track(`[MicrosoftTeamsLoginButton] auth failed: code=${code}, errorCode=${e?.errorCode}, message=${(e?.message ?? '').slice(0, 200)}`);
      }
      dispatch(loginActions.setSmartError(enrichError(code)));
      props.onError(code);
    } finally {
      dispatch(setAuthLoading(false));
    }
  }

  if (accounts && accounts.length > 1) {
    return (
      <>
        {accounts.map((account, key) => <OutlineButton key={key}
                                                       dataTest="outlookLogin"
                                                       className="login__button"
                                                       text={`login.with-office365`}
                                                       subtext={account.username}
                                                       click={() => onClick(account)}
                                                       fullWidth={true}
                                                       size={ButtonSize.Large} imageUrl="/assets/icons/microsoft-office-logo.svg" />)}
      </>
    )
  } else {
    return (
      <>
        <OutlineButton dataTest="outlookLogin" className="login__button" text="login.with-office365"
                       click={() => onClick()} fullWidth={true} size={ButtonSize.Large}
                       imageUrl="/assets/icons/microsoft-office-logo.svg"/>
      </>
    )
  }

}

interface Props {
  onError: (message: string) => void;
}
